import { s, typographyTokens } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { ChartEmptyState } from '@/ui/composites/charts/ChartEmptyState';
import { Panel } from '@/ui/composites/Panel';
import { Stack } from '@/ui/layout/Stack';
import { BarChart, Box, LineChart } from '@/ui/primitives';
import { LineChartSkeleton } from '@/ui/primitives/LineChartSkeleton';
import { StackedBarChart, type StackedBarChartDatum } from '@/ui/primitives/StackedBarChart';
import { StackedBarChartSkeleton } from '@/ui/primitives/StackedBarChartSkeleton';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import type { UiTone } from '@/theme/uiTokens';
import type { ReactElement } from 'react';

type PipelineChartTab = 'status' | 'success-rate' | 'frequency' | 'duration';

type StackedSeries = { key: string; label: string; color?: string; colorKey?: UiTone };

type PipelinesMetricsChartPanelProps = {
  tabsContentGap: 'md';
  chartTab: PipelineChartTab;
  onChartTabChange: (nextTab: PipelineChartTab) => void;
  hasChartDateFilter: boolean;
  onChartDateClear: () => void;
  chartHeight: number;
  suppressChartAnimation: boolean;
  filteredStackedChartData: { data: StackedBarChartDatum[]; series: StackedSeries[] } | null;
  filteredSuccessRateChartData: Array<Record<string, string | number>> | null;
  filteredFrequencyChartData: Array<Record<string, string | number>> | null;
  filteredDurationChartData: Array<Record<string, string | number>> | null;
  successRateGuideLines: Array<{ value: number; color: string }>;
  frequencyGuideLines: Array<{ value: number; color: string }>;
  durationGuideLines: Array<{ value: number; color: string }>;
  onChartSelect: (dataIndex: number, seriesKey?: string) => void;
  showLineChartPoints: boolean;
  emptyStateTitle?: string;
};

const hasVisibleStackedChartValue = (
  data: Array<Record<string, string | number>>,
  series: Array<{ key: string }>,
): boolean =>
  data.some((row) =>
    series.some((seriesItem) => {
      const value = row[seriesItem.key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

const hasVisibleChartValue = (
  data: Array<Record<string, string | number>> | null,
  key: string,
): boolean =>
  Boolean(
    data?.some((row) => {
      const value = row[key];
      return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }),
  );

export const PipelinesMetricsChartPanel = ({
  tabsContentGap,
  chartTab,
  onChartTabChange,
  hasChartDateFilter,
  onChartDateClear,
  chartHeight,
  suppressChartAnimation,
  filteredStackedChartData,
  filteredSuccessRateChartData,
  filteredFrequencyChartData,
  filteredDurationChartData,
  successRateGuideLines,
  frequencyGuideLines,
  durationGuideLines,
  onChartSelect,
  showLineChartPoints,
  emptyStateTitle = 'No builds found',
}: PipelinesMetricsChartPanelProps): ReactElement => {
  return (
    <Panel padding="md" gap="none" contentGap="lg">
      <Stack gap={tabsContentGap}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" align="center" justify="space-between" sx={{ minWidth: 0 }}>
            <Tabs
              value={chartTab}
              onChange={(_, next) => {
                onChartTabChange(next as PipelineChartTab);
              }}
              aria-label="Pipelines chart tabs"
            >
              <Tab label="Status" value="status" />
              <Tab label="Success Rate" value="success-rate" />
              <Tab label="Frequency" value="frequency" />
              <Tab label="Duration" value="duration" />
            </Tabs>
            {hasChartDateFilter ? (
              <Box
                component="button"
                type="button"
                onClick={onChartDateClear}
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  fontSize: typographyTokens.captionSmall.fontSize,
                  mr: s('md'),
                  background: 'none',
                  border: 'none',
                  padding: s('none'),
                  userSelect: 'none',
                  font: 'inherit',
                  lineHeight: 'inherit',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                RESET
              </Box>
            ) : null}
          </Stack>
        </Box>
        {chartTab === 'status' ? (
          filteredStackedChartData ? (
            hasVisibleStackedChartValue(
              filteredStackedChartData.data,
              filteredStackedChartData.series,
            ) ? (
              <StackedBarChart
                data={filteredStackedChartData.data}
                series={filteredStackedChartData.series}
                height={chartHeight}
                skipAnimation={suppressChartAnimation}
                onSelectBar={onChartSelect}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
              </Box>
            )
          ) : (
            <StackedBarChartSkeleton height={chartHeight} />
          )
        ) : chartTab === 'success-rate' ? (
          filteredSuccessRateChartData ? (
            hasVisibleChartValue(filteredSuccessRateChartData, 'passRate') ? (
              <BarChart
                data={filteredSuccessRateChartData}
                series={[{ key: 'passRate', label: '', color: uiTokens.statusColors.green }]}
                height={chartHeight}
                showLegend={false}
                yReferenceLines={successRateGuideLines}
                onSelectBar={(dataIndex) => {
                  onChartSelect(dataIndex);
                }}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
              </Box>
            )
          ) : (
            <StackedBarChartSkeleton height={chartHeight} />
          )
        ) : chartTab === 'frequency' ? (
          filteredFrequencyChartData ? (
            hasVisibleChartValue(filteredFrequencyChartData, 'frequency') ? (
              <LineChart
                data={filteredFrequencyChartData}
                series={[{ key: 'frequency', label: 'Builds', color: 'var(--color-pink)' }]}
                height={chartHeight}
                showLegend={false}
                yReferenceLines={frequencyGuideLines}
                onSelectBar={(dataIndex) => {
                  onChartSelect(dataIndex);
                }}
                showPoints={showLineChartPoints || filteredFrequencyChartData.length === 1}
              />
            ) : (
              <Box
                sx={{
                  height: chartHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
              </Box>
            )
          ) : (
            <LineChartSkeleton height={chartHeight} />
          )
        ) : filteredDurationChartData ? (
          hasVisibleChartValue(filteredDurationChartData, 'duration') ? (
            <LineChart
              data={filteredDurationChartData}
              series={[{ key: 'duration', label: '' }]}
              height={chartHeight}
              showLegend={false}
              yReferenceLines={durationGuideLines}
              onSelectBar={(dataIndex) => {
                onChartSelect(dataIndex);
              }}
              showPoints={showLineChartPoints || filteredDurationChartData.length === 1}
            />
          ) : (
            <Box
              sx={{
                height: chartHeight,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChartEmptyState title={emptyStateTitle} body="Try adjusting filters or search." />
            </Box>
          )
        ) : (
          <LineChartSkeleton height={chartHeight} />
        )}
      </Stack>
    </Panel>
  );
};
